/**
 * Application constants
 * Centralized location for magic strings and configuration values
 */

/**
 * Lesson status values
 */
export const LESSON_STATUS = {
  SCHEDULED: "scheduled",
  COMPLETED: "completed",
  NO_SHOW: "no_show",
  CANCELLED: "cancelled",
} as const;

export type LessonStatus = (typeof LESSON_STATUS)[keyof typeof LESSON_STATUS];

/**
 * Student status values
 */
export const STUDENT_STATUS = {
  TRIAL: "trial",
  ACTIVE: "active",
  PAUSED: "paused",
  INACTIVE: "inactive",
} as const;

export type StudentStatus = (typeof STUDENT_STATUS)[keyof typeof STUDENT_STATUS];

/**
 * Payment status values
 */
export const PAYMENT_STATUS = {
  PENDING: "pending",
  PAID: "paid",
  FAILED: "failed",
  REFUNDED: "refunded",
  CREDITED: "credited",
  WAIVED: "waived",
  UNPAID: "unpaid",
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

/**
 * Slot duration for availability (in minutes)
 */
export const SLOT_DURATION_MINUTES = 30;

/**
 * Password requirements
 */
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 12,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBER: true,
  REQUIRE_SPECIAL_CHAR: true,
} as const;

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
  UNAUTHORIZED: "Unauthorized. Please log in.",
  NOT_FOUND: "Resource not found.",
  FORBIDDEN: "You don't have permission to access this resource.",
  VALIDATION_ERROR: "Validation error. Please check your input.",
  INTERNAL_ERROR: "An internal error occurred. Please try again later.",
  RATE_LIMIT_EXCEEDED: "Too many requests. Please try again later.",
  INVALID_CREDENTIALS: "Invalid credentials.",
  EMAIL_IN_USE: "An account with this email already exists.",
  WEAK_PASSWORD: "Password does not meet security requirements.",
} as const;

/**
 * Default timezone
 */
export const DEFAULT_TIMEZONE = "America/New_York";

/**
 * Currency
 */
export const DEFAULT_CURRENCY = "usd";

/**
 * Valid lesson status values as array
 */
export const VALID_LESSON_STATUSES = Object.values(LESSON_STATUS);

/**
 * Valid student status values as array
 */
export const VALID_STUDENT_STATUSES = Object.values(STUDENT_STATUS);

/**
 * Valid payment status values as array
 */
export const VALID_PAYMENT_STATUSES = Object.values(PAYMENT_STATUS);

